import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { PackageOrder } from '../types';

// ============================================================================
// PAYMENT MODAL - Ödeme Al Modal
// ============================================================================

export interface PosOption {
  id: string;
  label: string;
  bankAccountId: string;
  bankAccountName?: string | null;
  subAccountId?: string | null;
}

export interface OtherPaymentOption {
  id: string;
  label: string;
  cashboxId: string;
  cashboxName: string | null;
}

export interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  selectedOrder: PackageOrder | null;
  isProcessingPayment: boolean;
  cashPaymentEnabled: boolean;
  posOptions: PosOption[];
  otherOptions?: OtherPaymentOption[];
  onCashPayment: () => void;
  onPosPayment: (posOption: any) => void;
  /** Karma ödeme (nakit + POS); yalnız telefon / paket siparişinde gösterilir. */
  onMixedPayment?: (posOption: PosOption, cashAmount: number) => void;
  onOtherPayment?: (option: OtherPaymentOption) => void;
  onOnlinePayment?: () => void;
  onPavoP2pPayment?: () => void;
  /** Secili siparisin Pavo'ya gonderilmis ve henuz tamamlanmamis bir odemesi varsa true */
  hasActivePavoPayment?: boolean;
  /** Pavo takip modalini acar */
  onOpenPavoTracking?: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  selectedOrder,
  isProcessingPayment,
  cashPaymentEnabled,
  posOptions,
  otherOptions,
  onCashPayment,
  onPosPayment,
  onMixedPayment,
  onOtherPayment,
  onOnlinePayment,
  onPavoP2pPayment,
  hasActivePavoPayment = false,
  onOpenPavoTracking,
}) => {
  const isPlatformOrder = selectedOrder && ['getir', 'migros', 'yemeksepeti', 'trendyol'].includes(selectedOrder.source);
  // Pavo ödemesi bekleniyorsa tüm ödeme yöntemleri kilitli olmali
  const buttonsDisabled = isProcessingPayment || hasActivePavoPayment;
  // Karma: kartla ödenecek kısım ilk POS seçeneğiyle (birden çoksa seçilir)
  const mixedAllowed = !!onMixedPayment && !isPlatformOrder && cashPaymentEnabled && posOptions.length > 0;
  const [mixedOpen, setMixedOpen] = useState(false);
  const [mixedCash, setMixedCash] = useState('');
  const [mixedPosId, setMixedPosId] = useState<string>('');
  useEffect(() => {
    if (!open) return;
    setMixedOpen(false);
    setMixedCash('');
    setMixedPosId(posOptions[0]?.id || '');
  }, [open, selectedOrder?.id, posOptions]);
  const total = selectedOrder?.totalAmount ?? 0;
  const mixedCashValue = Number(mixedCash.replace(',', '.'));
  const mixedValid = Number.isFinite(mixedCashValue) && mixedCashValue > 0 && mixedCashValue < total;
  const mixedPos = posOptions.find((p) => p.id === mixedPosId) || posOptions[0];
  const handleClose = () => {
    if (!isProcessingPayment) {
      onClose();
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        fontWeight: 'bold', 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          💳 Ödeme Al
        </Box>
        <IconButton 
          onClick={handleClose}
          sx={{ color: 'white' }}
          disabled={isProcessingPayment}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {selectedOrder && (
          <>
            {/* Sipariş Bilgileri */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Sipariş Bilgileri
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Sipariş No:</Typography>
                <Typography variant="body2" fontWeight="bold">{selectedOrder.shortOrderNo}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Müşteri:</Typography>
                <Typography variant="body2" fontWeight="bold">{selectedOrder.customerName}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" color="primary">Toplam:</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  ₺{selectedOrder.totalAmount.toFixed(2)}
                </Typography>
              </Box>
            </Paper>
            
            {/* Pavo beklemede uyari */}
            {hasActivePavoPayment && (
              <Alert
                severity="info"
                variant="filled"
                icon={<PointOfSaleIcon />}
                sx={{ mb: 2, bgcolor: '#7c4dff', fontWeight: 600 }}
                action={
                  onOpenPavoTracking ? (
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      onClick={onOpenPavoTracking}
                      sx={{ borderColor: '#fff', color: '#fff', whiteSpace: 'nowrap' }}
                    >
                      Takip / İptal
                    </Button>
                  ) : undefined
                }
              >
                Bu sipariş için Pavo ödemesi gönderildi. Cihazda onay bekleniyor, başka ödeme alınamaz.
              </Alert>
            )}

            {/* Ödeme Yöntemleri - İki Sütun */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Ödeme Yöntemi Seçin
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Sol Sütun: Nakit + POS + Online */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Nakit / POS
                </Typography>
                {cashPaymentEnabled && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={onCashPayment}
                    disabled={buttonsDisabled}
                    sx={{ 
                      py: 1.5, 
                      bgcolor: '#4CAF50', 
                      fontSize: '0.9rem', 
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: '#388E3C' },
                    }}
                  >
                    {isProcessingPayment ? (
                      <CircularProgress size={18} color="inherit" sx={{ mr: 0.5 }} />
                    ) : '💵'}{' '}NAKİT
                  </Button>
                )}
                {posOptions.map((posOption) => (
                  <Button
                    key={posOption.id}
                    variant="contained"
                    fullWidth
                    onClick={() => onPosPayment(posOption)}
                    disabled={buttonsDisabled}
                    sx={{ 
                      py: 1.5, 
                      bgcolor: '#2196F3', 
                      fontSize: '0.9rem', 
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: '#1976D2' },
                    }}
                  >
                    {isProcessingPayment ? (
                      <CircularProgress size={18} color="inherit" sx={{ mr: 0.5 }} />
                    ) : '💳'}{' '}{posOption.label}
                  </Button>
                ))}
                {mixedAllowed && (
                  <Button
                    variant={mixedOpen ? 'outlined' : 'contained'}
                    fullWidth
                    onClick={() => setMixedOpen((v) => !v)}
                    disabled={buttonsDisabled}
                    sx={{
                      py: 1.5,
                      bgcolor: mixedOpen ? undefined : '#00897B',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: mixedOpen ? undefined : '#00796B' },
                    }}
                  >
                    🔀 KARMA (NAKİT + POS)
                  </Button>
                )}
                {mixedAllowed && mixedOpen && (
                  <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      size="small"
                      autoFocus
                      label="Nakit alınan"
                      value={mixedCash}
                      onChange={(e) => setMixedCash(e.target.value.replace(/[^0-9.,]/g, ''))}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
                    />
                    {posOptions.length > 1 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {posOptions.map((p) => (
                          <Chip key={p.id} label={p.label} size="small"
                            color={mixedPos?.id === p.id ? 'primary' : 'default'}
                            onClick={() => setMixedPosId(p.id)} />
                        ))}
                      </Box>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      POS ile: ₺{(mixedValid ? total - mixedCashValue : 0).toFixed(2)}
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={buttonsDisabled || !mixedValid || !mixedPos}
                      onClick={() => mixedPos && onMixedPayment?.(mixedPos, Math.round(mixedCashValue * 100) / 100)}
                    >
                      {isProcessingPayment ? <CircularProgress size={18} color="inherit" /> : 'Karma ödemeyi al'}
                    </Button>
                  </Paper>
                )}
                {isPlatformOrder && onOnlinePayment && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={onOnlinePayment}
                    disabled={buttonsDisabled}
                    sx={{
                      py: 1.5,
                      bgcolor: '#7B1FA2',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: '#6A1B9A' },
                    }}
                  >
                    {isProcessingPayment ? (
                      <CircularProgress size={18} color="inherit" sx={{ mr: 0.5 }} />
                    ) : '🌐'}{' '}ONLİNE ÖDEME
                  </Button>
                )}
                {onPavoP2pPayment && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={onPavoP2pPayment}
                    disabled={buttonsDisabled}
                    sx={{
                      py: 1.5,
                      bgcolor: '#7c4dff',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: '#651fff' },
                    }}
                  >
                    {isProcessingPayment ? (
                      <CircularProgress size={18} color="inherit" sx={{ mr: 0.5 }} />
                    ) : '📱'}{' '}PAVOPAY P2P
                  </Button>
                )}
              </Box>

              {/* Sağ Sütun: Diğer Ödeme Yöntemleri */}
              {otherOptions && otherOptions.length > 0 && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Diğer Yöntemler
                  </Typography>
                  {otherOptions.map((option) => (
                    <Button
                      key={option.id}
                      variant="contained"
                      fullWidth
                      onClick={() => onOtherPayment?.(option)}
                      disabled={buttonsDisabled}
                      sx={{
                        py: 1.5,
                        bgcolor: '#FF9800',
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        '&:hover': { bgcolor: '#F57C00' },
                      }}
                    >
                      {isProcessingPayment ? (
                        <CircularProgress size={18} color="inherit" sx={{ mr: 0.5 }} />
                      ) : '🎫'}{' '}{option.label}
                    </Button>
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button 
          variant="outlined" 
          onClick={handleClose}
          disabled={isProcessingPayment}
        >
          İptal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;
